// @ts-check
import EVENTS from './mini-cart-events.js';

class HeaderCartDrawer extends HTMLElement {
  connectedCallback() {
    this._onProductAdded = this.handleProductAdded.bind(this);
    document.addEventListener(EVENTS.ajaxProductAdded, this._onProductAdded);
    this.initQuantityControls();
    window.addEventListener('pageshow', this.onPageShow);
  }

  disconnectedCallback() {
    document.removeEventListener(
      EVENTS.ajaxProductAdded,
      this._onProductAdded
    );
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Re-sync the mini-cart count when navigating back from /checkout.
   * Covers both bfcache restores (event.persisted === true) AND full reloads
   * triggered by the browser Back button after Shopify's hosted checkout redirect
   * (event.persisted === false), where the cached count may be stale.
   * @param {PageTransitionEvent} _event
   */
  onPageShow = (_event) => {
    this.ensureMiniCartCountIsCorrect();
  };

  /**
   * Fetch the latest cart item count and update the mini-cart drawer UI.
   */
  ensureMiniCartCountIsCorrect = async () => {
    const bubble = /** @type {HTMLElement | null} */ (this.querySelector('.cart-count-bubble'));
    const viewCartCountEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-view-cart-count]'));
    const itemsEl = /** @type {HTMLElement | null} */ (this.querySelector('.cart__items'));

    if (!bubble) return;

    try {
      const response = await fetch('/cart.js', { cache: 'no-store' });
      if (!response.ok) throw new Error('Cart fetch failed');
      const cart = await response.json();
      const itemCount = typeof cart.item_count === 'number' ? cart.item_count : 0;

      bubble.textContent = String(itemCount);
      bubble.hidden = itemCount === 0;

      if (itemsEl) itemsEl.dataset.count = String(itemCount);

      if (viewCartCountEl) viewCartCountEl.textContent = `(${String(itemCount)})`;
    } catch (err) {
      // Keep existing UI if cart cannot be read
    }
  };

  /**
   * @param {CustomEvent} evt
   */
  handleProductAdded(evt) {
    const response = evt.detail?.product;
    if (!response) return;

    // Store the last added line item key so we can show only that item in the mini cart
    // cart/add.js responses usually include `key` for the added line item.
    this._lastAddedKey = response.key || response.items?.[0]?.key || null;

    const sectionHTML = response.sections?.['cart-ajax'];
    if (sectionHTML) {
      this.updateDrawer(sectionHTML, this._lastAddedKey);
    }

    // Open the drawer
    document.dispatchEvent(
      new CustomEvent(EVENTS.cartOpen, { bubbles: true })
    );
  }

  /**
   * @param {string} sectionHTML
   * @param {string | null} lastAddedKey
   */
  updateDrawer(sectionHTML, lastAddedKey = null) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(sectionHTML, 'text/html');

    /**
     * @param {string} key
     */
    const escapeKey = (key) => {
      if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(key);
      return String(key).replace(/"/g, '\\"');
    };

    // Full items list (cart-ajax section)
    const newItemsBlock = doc.querySelector('.cart-ajax-section .cart__items') || doc.querySelector('.cart__items');
    const newSubtotal = doc.querySelector('[data-subtotal]');
    const newDiscounts = doc.querySelector('[data-discounts]');
    const newCount = /** @type {HTMLElement | null} */ (doc.querySelector('.cart-count-bubble'));

    const productsEl = this.querySelector('[data-products]');
    const footerSubtotalEl = this.querySelector('.cart-drawer__footer [data-subtotal]');
    const footerDiscountsEl = this.querySelector('.cart-drawer__footer [data-discounts]');

    if (productsEl && newItemsBlock) {
      // By default: show only the last added item (requested behavior)
      // Fallback: if we can't find that item, show the first cart item, or the empty state.
      if (lastAddedKey) {
        const lastItemEl = newItemsBlock.querySelector(`.cart-item[data-key="${escapeKey(lastAddedKey)}"]`);
        if (lastItemEl) {
          productsEl.innerHTML = lastItemEl.outerHTML;
        } else {
          const firstItemEl = newItemsBlock.querySelector('.cart-item');
          productsEl.innerHTML = firstItemEl ? firstItemEl.outerHTML : newItemsBlock.innerHTML;
        }
      } else {
        const firstItemEl = newItemsBlock.querySelector('.cart-item');
        productsEl.innerHTML = firstItemEl ? firstItemEl.outerHTML : newItemsBlock.innerHTML;
      }
    }
    if (footerSubtotalEl && newSubtotal) {
      footerSubtotalEl.textContent = newSubtotal.textContent?.trim() ?? '';
    }
    if (footerDiscountsEl && newDiscounts) {
      footerDiscountsEl.innerHTML = newDiscounts.innerHTML;
    }

    // Update all cart count bubbles in header (desktop and mobile)
    if (newCount) {
      document.querySelectorAll('.cart-count-bubble').forEach((el) => {
        const htmlEl = /** @type {HTMLElement} */ (el);
        htmlEl.textContent = newCount.textContent;
        htmlEl.hidden = newCount.hidden;
      });
      // Update View Cart button product count in mini cart footer
      const viewCartCountEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-view-cart-count]'));
      if (viewCartCountEl) {
        viewCartCountEl.textContent = `(${newCount.textContent?.trim() ?? ''})`;
      }
    }

    this.initQuantityControls();
  }

  initQuantityControls() {
    // Use event delegation so we don't duplicate listeners after innerHTML updates
    /**
     * @param {string} selector
     * @param {(target: HTMLElement, e: MouseEvent) => void} handler
     */
    const delegate = (selector, handler) => /** @param {MouseEvent} e */(e) => {
      const target = /** @type {HTMLElement | null} */ (/** @type {HTMLElement} */ (e.target).closest(selector));
      if (!target || !this.contains(target)) return;
      handler(target, e);
    };

    this._qtyDelegates = this._qtyDelegates || {};
    if (!this._qtyDelegates.bound) {
      this._qtyDelegates.bound = true;
      this.addEventListener('click', delegate('.js-qty-minus', (btn, e) => {
        e.preventDefault();
        const line = btn.dataset.line;
        const input = /** @type {HTMLInputElement | null} */ (this.querySelector(`.cart-item__qty-input[data-line="${line}"]`));
        if (!input) return;
        const newQty = Math.max(0, parseInt(input.value, 10) - 1);
        input.value = String(newQty);
        this.changeQuantity(line, newQty);
      }));
      this.addEventListener('click', delegate('.js-qty-plus', (btn, e) => {
        e.preventDefault();
        const line = btn.dataset.line;
        const input = /** @type {HTMLInputElement | null} */ (this.querySelector(`.cart-item__qty-input[data-line="${line}"]`));
        if (!input) return;
        const newQty = parseInt(input.value, 10) + 1;
        input.value = String(newQty);
        this.changeQuantity(line, newQty);
      }));
      this.addEventListener('click', delegate('.js-remove-item', (btn, e) => {
        e.preventDefault();
        this.changeQuantity(btn.dataset.line, 0);
      }));
    }
  }

  /**
   * @param {string | undefined} line
   * @param {number} quantity
   */
  async changeQuantity(line, quantity) {
    try {
      const shopify = /** @type {any} */ (window.Shopify);
      const root = (shopify && shopify.routes && shopify.routes.root) ? shopify.routes.root : '/';
      const response = await fetch(
        `${root}cart/change.js`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            line: parseInt(String(line), 10),
            quantity,
            sections: 'cart-ajax'
          })
        }
      );

      const data = await response.json();

      if (data.sections?.['cart-ajax']) {
        // Keep showing only the last-added item if it still exists
        this.updateDrawer(data.sections['cart-ajax'], this._lastAddedKey);
      }

      document.dispatchEvent(
        new CustomEvent(EVENTS.cartUpdated, {
          bubbles: true,
          detail: { cart: data }
        })
      );
    } catch (err) {
      console.error('Cart update failed:', err);
    }
  }
}

customElements.define('header-cart-drawer', HeaderCartDrawer);
export default HeaderCartDrawer;
